'use strict';
import React, { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Button, FormInput } from '@/components/index.jsx';
import { useSearchUsersQuery, useUserRegistrationEligibilityQuery } from '@/redux/services/portal/admin.api';
import './AddNewUser.scss';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const AddNewUserModal = ({
	open,
	onCancel,
	onSubmit,
	isSubmitting,
	errorMessage,
	initialValues,
	isEdit,
	submitLabel,
}) => {
	const [formValues, setFormValues] = useState({
		firstName: '',
		lastName: '',
		email: '',
	});
	const [touched, setTouched] = useState({
		firstName: false,
		lastName: false,
		email: false,
	});

	const resetForm = useCallback(() => {
		setFormValues({
			firstName: '',
			lastName: '',
			email: '',
		});
		setTouched({
			firstName: false,
			lastName: false,
			email: false,
		});
	}, []);

	useEffect(() => {
		if (open) {
			setFormValues({
				firstName: initialValues?.firstName || '',
				lastName: initialValues?.lastName || '',
				email: initialValues?.email || '',
			});
			setTouched({
				firstName: false,
				lastName: false,
				email: false,
			});
		}
	}, [open, initialValues]);

	const handleChange = (field) => (event) => {
		setFormValues((prev) => ({
			...prev,
			[field]: event.target.value,
		}));
		setTouched((prev) => ({
			...prev,
			[field]: true,
		}));
	};

	const handleBlur = (field) => () => {
		setTouched((prev) => ({
			...prev,
			[field]: true,
		}));
	};

	const errors = useMemo(() => {
		const firstName = formValues.firstName.trim();
		const lastName = formValues.lastName.trim();
		const email = formValues.email.trim();
		const isEmailValid = email.length > 0 && EMAIL_REGEX.test(email);

		return {
			firstName: firstName.length === 0 ? 'First name is required' : '',
			lastName: lastName.length === 0 ? 'Last name is required' : '',
			email: email.length === 0
				? 'Email is required'
				: isEmailValid
					? ''
					: 'Enter a valid email address',
		};
	}, [formValues]);

	const normalizedEmail = formValues.email.trim().toLowerCase();
	const shouldCheckExisting = !isEdit && normalizedEmail.length > 0 && EMAIL_REGEX.test(normalizedEmail);
	const { data: existingUsers, isFetching: isCheckingExisting } = useSearchUsersQuery(
		{ searchText: normalizedEmail.toUpperCase(), skip: 0, take: 1 },
		{ skip: !shouldCheckExisting }
	);
	const hasExistingEmail = Array.isArray(existingUsers)
		? existingUsers.some((user) => (user.email || user.Email || '').toLowerCase() === normalizedEmail)
		: false;

	const shouldCheckEligibility = shouldCheckExisting && !hasExistingEmail;
	const { data: registrationEligibility, isFetching: isCheckingEligibility } = useUserRegistrationEligibilityQuery(
		{ email: normalizedEmail },
		{ skip: !shouldCheckEligibility }
	);
	const isRegistrationBlocked = shouldCheckEligibility
		&& !isCheckingEligibility
		&& registrationEligibility
		&& !registrationEligibility.allowed;

	const resolvedErrors = useMemo(() => {
		if (errors.email || !shouldCheckExisting) {
			return errors;
		}
		if (hasExistingEmail) {
			return { ...errors, email: 'User already exists' };
		}
		if (isRegistrationBlocked) {
			return {
				...errors,
				email: registrationEligibility?.message
					|| 'This email address is not allowed for registration.',
			};
		}
		return { ...errors, email: '' };
	}, [errors, hasExistingEmail, shouldCheckExisting, isRegistrationBlocked, registrationEligibility?.message]);

	const isValid = useMemo(
		() => Object.values(resolvedErrors).every((error) => !error) && (isEdit || !hasExistingEmail),
		[resolvedErrors, hasExistingEmail, isEdit]
	);

	const handleCancel = () => {
		resetForm();
		onCancel();
	};

	const handleSubmit = () => {
		if (!isValid || isSubmitting) return;
		const payload = {
			firstName: formValues.firstName.trim(),
			lastName: formValues.lastName.trim(),
			email: formValues.email.trim(),
		};
		onSubmit(payload);
	};

	const renderFieldError = (field) => {
		if (!touched[field] || !resolvedErrors[field]) return null;
		return <div className="InputError">{resolvedErrors[field]}</div>;
	};

	// Show wait spinner until the add/update user endpoint returns
	if (isSubmitting) {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 300, py: 2 }}>
				<CircularProgress />
				<Typography variant="body2">
					{isEdit ? 'Saving user…' : 'Adding user…'}
				</Typography>
			</Box>
		);
	}

	return (
		<div className="AddUsersContainer">
			<hr />
			<div className="Name" style={{ display: 'inline-block' }}>
				<FormInput
					width={'small'}
					placeHolder={'First Name'}
					onChange={handleChange('firstName')}
					onBlur={handleBlur('firstName')}
					value={formValues.firstName}
					name="firstName"
					autoComplete="given-name"
				/>
				<div className="FieldError">
					{renderFieldError('firstName')}
				</div>
			</div>
			<div className="Name" style={{ display: 'inline-block', paddingLeft: '15px' }}>
				<FormInput
					width={'small'}
					placeHolder={'Last Name'}
					onChange={handleChange('lastName')}
					onBlur={handleBlur('lastName')}
					value={formValues.lastName}
					name="lastName"
					autoComplete="family-name"
				/>
				<div className="FieldError">
					{renderFieldError('lastName')}
				</div>
			</div>
			<div className="Email">
				<FormInput
					width={'normal'}
					placeHolder={'Email'}
					onChange={handleChange('email')}
					onBlur={handleBlur('email')}
					value={formValues.email}
					type="email"
					name="email"
					autoComplete="email"
				/>
				<div className="FieldError">
					{renderFieldError('email')}
				</div>
			</div>
			<div className="UserActions">
				{errorMessage ? <div className="SubmitError">{errorMessage}</div> : null}
				<Button className="alt" onClick={handleCancel}>Cancel</Button>
				<Button onClick={handleSubmit} disabled={!isValid || isSubmitting || (!isEdit && (isCheckingExisting || isCheckingEligibility))}>
					{isSubmitting
						? (isEdit ? 'Saving...' : 'Adding...')
						: (isCheckingExisting || isCheckingEligibility)
							? 'Checking...'
							: (submitLabel || (isEdit ? 'Edit User' : 'Add User'))}
				</Button>
			</div>
		</div>
	);
};

AddNewUserModal.propTypes = {
	open: PropTypes.bool,
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	isSubmitting: PropTypes.bool,
	errorMessage: PropTypes.string,
	initialValues: PropTypes.shape({
		firstName: PropTypes.string,
		lastName: PropTypes.string,
		email: PropTypes.string,
	}),
	isEdit: PropTypes.bool,
	submitLabel: PropTypes.string,
};

AddNewUserModal.defaultProps = {
	open: false,
	isSubmitting: false,
	errorMessage: '',
	initialValues: null,
	isEdit: false,
	submitLabel: '',
};

export default AddNewUserModal;
